import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

// Import all translation files
import { commonEn } from './translations/Common/en';
import { commonIs } from './translations/Common/is';
import { aboutEn } from './translations/About/en';
import { aboutIs } from './translations/About/is';
import { homeEn } from './translations/Home/en';
import { homeIs } from './translations/Home/is';
import { menuEn } from './translations/Menu/en';
import { menuIs } from './translations/Menu/is';
import { contactEn } from './translations/Contact/en';
import { contactIs } from './translations/Contact/is';
import { reservationsEn } from './translations/Reservations/en';
import { reservationsIs } from './translations/Reservations/is';
import { wineListEn } from './translations/WineList/en';
import { wineListIs } from './translations/WineList/is';
import { careersEn } from './translations/Careers/en';
import { careersIs } from './translations/Careers/is';
import { notFoundEn } from './translations/NotFound/en';
import { notFoundIs } from './translations/NotFound/is';

type Language = 'en' | 'is';

// Combine all translations
const translations = {
  en: {
    common: commonEn,
    about: aboutEn,
    home: homeEn,
    menu: menuEn,
    contact: contactEn,
    reservations: reservationsEn,
    wineList: wineListEn,
    careers: careersEn,
    notFound: notFoundEn,
  },
  is: {
    common: commonIs,
    about: aboutIs,
    home: homeIs,
    menu: menuIs,
    contact: contactIs,
    reservations: reservationsIs,
    wineList: wineListIs,
    careers: careersIs,
    notFound: notFoundIs,
  },
};

type Translations = typeof translations.en;

// Utility functions for scroll position management
const saveScrollPosition = () => {
  const scrollData = {
    window: {
      x: window.scrollX || window.pageXOffset,
      y: window.scrollY || window.pageYOffset,
    },
    containers: [] as Array<{
      selector: string;
      x: number;
      y: number;
    }>,
  };

  // Save scroll positions for common scrollable containers
  const scrollableSelectors = [
    '.scrollable',
    '.overflow-auto',
    '.overflow-x-auto',
    '.overflow-y-auto',
    '[data-scrollable]',
  ];

  scrollableSelectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    elements.forEach((element, index) => {
      if (element.scrollLeft > 0 || element.scrollTop > 0) {
        scrollData.containers.push({
          selector: `${selector}:nth-of-type(${index + 1})`,
          x: element.scrollLeft,
          y: element.scrollTop,
        });
      }
    });
  });

  return scrollData;
};

const restoreScrollPosition = (scrollData: ReturnType<typeof saveScrollPosition>) => {
  // Use requestAnimationFrame to ensure DOM has updated after language change
  requestAnimationFrame(() => {
    // Restore window scroll position
    window.scrollTo({
      left: scrollData.window.x,
      top: scrollData.window.y,
      behavior: 'instant',
    });

    // Restore container scroll positions
    scrollData.containers.forEach(({ selector, x, y }) => {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollLeft = x;
        element.scrollTop = y;
      }
    });

    // Fallback for browsers that don't support 'instant' behavior
    if (window.scrollX !== scrollData.window.x || window.scrollY !== scrollData.window.y) {
      window.scrollTo(scrollData.window.x, scrollData.window.y);
    }
  });
};
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language, preserveScroll?: boolean) => void;
  t: Translations;
  isTransitioning: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return 'en';
    }

    // Check localStorage for saved language preference
    try {
      const savedLanguage = localStorage.getItem('selectedLanguage');
      if (savedLanguage === 'en' || savedLanguage === 'is') {
        return savedLanguage;
      }
    } catch (error) {
      console.warn('Failed to load language preference from localStorage:', error);
    }

    // Default to English
    return 'en';
  });
  const [currentTranslations, setCurrentTranslations] = useState<Translations>(translations.en);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleLanguageChange = (lang: Language, preserveScroll: boolean = true) => {
    if (lang === language) return; // Prevent unnecessary transitions

    setIsTransitioning(true);
    let scrollData: ReturnType<typeof saveScrollPosition> | null = null;

    // Save current scroll position if preservation is enabled
    if (preserveScroll) {
      scrollData = saveScrollPosition();
    }

    // Add a subtle delay for smooth transition
    setTimeout(() => {
      // Update language and translations
      setLanguage(lang);
      setCurrentTranslations(translations[lang]);

      // Save language preference to localStorage
      try {
        localStorage.setItem('selectedLanguage', lang);
      } catch (error) {
        console.warn('Failed to save language preference to localStorage:', error);
      }

      // Restore scroll position after state update
      if (preserveScroll && scrollData) {
        setTimeout(() => {
          restoreScrollPosition(scrollData!);
          setIsTransitioning(false);
        }, 50);
      } else {
        setIsTransitioning(false);
      }
    }, 100);
  };
  useEffect(() => {
    setCurrentTranslations(translations[language]);
  }, [language]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage: handleLanguageChange,
        t: currentTranslations,
        isTransitioning,
      }}
    >
      <Helmet htmlAttributes={{ lang: language }} />
      <Helmet>
        <link rel="alternate" href="https://rossopomodoro.is/" hreflang="x-default" />
        <link rel="alternate" href="https://rossopomodoro.is/" hreflang="en" />
        <link rel="alternate" href="https://rossopomodoro.is/" hreflang="is" />
      </Helmet>
      <motion.div
        key={language}
        initial={{ opacity: 0.95 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.2,
          ease: 'easeOut',
        }}
        className={isTransitioning ? 'pointer-events-none' : ''}
      >
        {children}
      </motion.div>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
